



















































































import { Component, Vue } from 'vue-property-decorator';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import { Action, Getter } from 'vuex-class';
import { workspacesApi } from '@/workspaces/workspaces';
import Toast from '@/shared/services/toast';
import ReadonlyCopyableField from '@/shared/components/ReadonlyCopyableField.vue';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import TitledSection from '@/shared/components/TitledSection.vue';
import { messagingWorkspacesApi } from '@/messaging/messaging';
import { AddOrReplaceMessagingWorkspaceRequest } from '@/messaging/models/addOrReplaceMessagingWorkspaceRequest';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { UsageLimitType } from '@/features/settings/models/usageLimitType';
import { UpdateWorkspaceViewModel } from '@/workspaces/models/request/updateWorkspaceViewModel';
import { MAX_INTEGER } from '@/shared/constants';
import { PROACTIVE_FLOWS_ENABLED } from '@/store/app/featureTypes';

@Component({
	components: {
		ReadonlyCopyableField,
		TitledSection,
		LoadingSpinner
	},
	mixins: [validationMixin],
	validations: {
		workspaceName: { required },
		messageRetentionInDays: { required, minValue: minValue(1), maxValue: maxValue(365) },
		appUserRetentionInDays: { required, minValue: minValue(1), maxValue: maxValue(365) },
		maxProactiveTriggers: { required, minValue: minValue(0), maxValue: maxValue(MAX_INTEGER) }
	}
})
export default class GeneralSettings extends Vue {

	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Getter public workspaceUsageLimit!: (usageLimitType: string) => number;
	@Getter public isWorkspaceFeatureEnabled!: (key: string) => boolean;
	@Action(HANDLE_SERIOUS_ERROR) public handleSeriousError: any;

	public workspaceName: string = '';
	public messageRetentionInDays: number = 90;
	public appUserRetentionInDays: number = 365;
	public maxProactiveTriggers: number = 0;

	public updateInProgress: boolean = false;
	public isLoading: boolean = true;
	public showUsageLimits: boolean = false;

	public async mounted() {
		try {
			if (this.isWorkspaceFeatureEnabled(PROACTIVE_FLOWS_ENABLED)) {
				this.showUsageLimits = true;
			}
			this.workspaceName = this.currentWorkspace.name;
			const workspaceFromMessaging = await messagingWorkspacesApi.getWorkspace(this.currentWorkspace.id);
			this.messageRetentionInDays = workspaceFromMessaging.messageRetentionInDays;
			this.appUserRetentionInDays = workspaceFromMessaging.appUserRetentionInDays;
			this.maxProactiveTriggers = this.workspaceUsageLimit(UsageLimitType.ProactiveTriggers);
		} catch (error) {
			if (error.status !== 404) {
				this.handleSeriousError(error.message);
			} else {
				this.messageRetentionInDays = 90;
				this.appUserRetentionInDays = 365;
			}
		} finally {
			this.isLoading = false;
		}
	}

	public async updateWorkspace() {
		if (!this.updateInProgress) {
			try {
				this.updateInProgress = true;
				const request = this.createUpdateRequest();
				await workspacesApi.update(this.currentWorkspace.id, request);
				this.currentWorkspace.name = request.name;
				this.currentWorkspace.usageLimits = request.usageLimits;

				await messagingWorkspacesApi.addOrReplace(this.createAddOrReplaceMessagingWorkspaceRequest());

				Toast.showSuccess(`Successfully updated workspace ${this.currentWorkspace.name}`);
			} catch (err) {
				Toast.showError(err.message);
			} finally {
				this.updateInProgress = false;
			}
		}
	}

	public getErrorMessage($v: any, prop: string, label: string) {
		if ($v[prop].$invalid) {
			if (!$v[prop].required) {
				return `${label} is required`;
			}
			if (!$v[prop].minValue) {
				return `${label} must not be less than ${$v[prop].$params.minValue.min}`;
			}
			if (!$v[prop].maxValue) {
				return `${label} must not be greater than ${$v[prop].$params.maxValue.max}`;
			}
		}
		return null;
	}

	private createAddOrReplaceMessagingWorkspaceRequest(): AddOrReplaceMessagingWorkspaceRequest {
		return {
			id: this.currentWorkspace.id,
			name: this.currentWorkspace.name,
			messageRetentionInDays: this.messageRetentionInDays,
			appUserRetentionInDays: this.appUserRetentionInDays
		};
	}

	private createUpdateRequest() {
		if (this.showUsageLimits) {
			return {
				name: this.workspaceName,
				usageLimits: { [UsageLimitType.ProactiveTriggers]: this.maxProactiveTriggers }
			} as UpdateWorkspaceViewModel;
		} else {
			return {
				name: this.workspaceName
			} as UpdateWorkspaceViewModel;
		}
	}
}
